import { Link } from "react-router-dom";
import { Lang, t } from "../i18n";
import { type HomeLocale } from "../homeContent";
import { aboutPath, cookiePath, icpRecord, privacyPath, publicSecurityRecord, termsPath } from "../legal";
import { officialXHandle, officialXProfileUrl } from "../social";
import { BrandLogo } from "./BrandLogo";

const contactEmail = "info@clawease.ai";

const hongKongCopy: Record<string, string> = {
  "footer.desc.1": "面向高價值請求的 AI 業務操作員",
  "footer.desc.2": "讓企業由聆聽、識別、處理到同步，完成整個業務流程。",
  "footer.legal.privacy": "私隱政策",
  "footer.legal.cookie": "Cookie 政策",
  "footer.legal.terms": "使用條款",
  "footer.legal.about": "關於我們",
  "footer.contact.title": "聯絡我們",
  "footer.contact.email": "電郵",
  "footer.contact.x": "官方 X",
};

const legalLinkClass =
  "relative z-30 inline-flex min-h-8 items-center text-white/40 no-underline transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#a855f7]";

export function Footer({ lang, compact = false, locale = lang }: { lang: Lang; compact?: boolean; locale?: HomeLocale }) {
  const hongKong = locale === "zh-HK";
  const text = (key: string) => hongKong ? hongKongCopy[key] ?? t(lang, key) : t(lang, key);

  if (compact) {
    return (
      <footer role="contentinfo" className="px-3 text-white sm:px-5 lg:px-8">
        <div className="compact-footer-frame mx-auto w-full max-w-[1820px] overflow-hidden rounded-t-[28px] bg-[#07080d] px-6 py-10 sm:px-10 lg:px-14 lg:py-12">
          <div className="mx-auto max-w-[1200px]">
            <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-end">
              <div>
                <BrandLogo variant="inverse" size="compact" />
                <p className="mt-5 max-w-[560px] text-[14px] font-medium leading-7 text-white/[.58]">{text("footer.desc.1")}</p>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-[13px] font-semibold md:max-w-[430px] md:justify-end">
                <a href={`mailto:${contactEmail}`} className="min-h-8 content-center font-bold text-[#8fe7ef] no-underline hover:text-white">{contactEmail}</a>
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-2 border-t border-white/10 pt-6 text-[12px] font-semibold text-white/[.36] sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-6">
              <span className="mr-auto text-left">{text("footer.copyright")}</span>
              <div className="flex flex-col items-end gap-2 text-right sm:ml-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-4">
                <Link to={privacyPath(lang)} className={legalLinkClass}>{text("footer.legal.privacy")}</Link>
                <Link to={cookiePath(lang)} className={legalLinkClass}>{text("footer.legal.cookie")}</Link>
                <Link to={termsPath(lang)} className={legalLinkClass}>{text("footer.legal.terms")}</Link>
                <Link to={aboutPath(lang)} className={legalLinkClass}>{text("footer.legal.about")}</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="px-3 text-white sm:px-5 lg:px-8">
      <div className={`relative isolate mx-auto w-full max-w-[1820px] overflow-hidden rounded-t-[28px] bg-[#03050b] px-6 pt-[4.5rem] sm:px-10 lg:px-14 lg:pt-20 ${hongKong ? "pb-8 sm:pb-10" : "pb-[12rem] sm:pb-[15rem] lg:pb-[18rem]"}`}>
        {!hongKong && (
          <>
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[540px] opacity-90"
          style={{
            background:
              "radial-gradient(circle at 28% 100%, rgba(126, 34, 206, .62), transparent 44%), radial-gradient(circle at 68% 100%, rgba(37, 99, 235, .38), transparent 40%), radial-gradient(circle at 50% 78%, rgba(217, 70, 239, .18), transparent 36%), linear-gradient(180deg, transparent 0%, rgba(18, 8, 38, .18) 34%, rgba(5, 9, 28, .86) 100%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[500px] opacity-80"
          style={{
            background:
              "repeating-linear-gradient(90deg, rgba(139, 92, 246, .20) 0 58px, rgba(10, 12, 29, .16) 58px 116px)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, .08) 18%, rgba(0, 0, 0, .55) 52%, black 100%)",
            maskImage: "linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, .08) 18%, rgba(0, 0, 0, .55) 52%, black 100%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-[-12px] z-0 bg-clip-text text-center text-[84px] font-black leading-none tracking-[-0.08em] text-transparent opacity-80 drop-shadow-[0_0_34px_rgba(168,85,247,.18)] sm:bottom-[-18px] sm:text-[150px] lg:bottom-[-24px] lg:text-[224px]"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(255,255,255,.08) 0%, rgba(126,34,206,.42) 34%, rgba(217,70,239,.34) 54%, rgba(37,99,235,.30) 78%, rgba(255,255,255,.06) 100%)",
          }}
        >
          CLAWEASE
        </div>
          </>
        )}

      <div className="relative z-20 mx-auto max-w-[1200px]">
        <div className="grid gap-10 md:grid-cols-[minmax(0,1fr)_auto] md:gap-16">
          <div>
            <BrandLogo variant="inverse" size="footer" />
            <p className="mt-8 max-w-[320px] text-[16px] font-semibold leading-8 tracking-[-0.01em] text-white/72">{text("footer.desc.1")}</p>
            <p className="mt-3 max-w-[360px] text-[14px] font-medium leading-7 tracking-[-0.01em] text-white/42">{text("footer.desc.2")}</p>
          </div>

          <div className="md:min-w-[260px] md:justify-self-end">
            <h3 className="mb-6 text-[18px] font-extrabold tracking-[-0.02em] text-white/95">{text("footer.contact.title")}</h3>
            <div className="space-y-5 text-[15px] font-semibold leading-8 tracking-[-0.01em] text-white/50">
              <p>
                {text("footer.contact.email")}:{" "}
                <a href={`mailto:${contactEmail}`} className="font-extrabold text-[#67e8f9] no-underline hover:text-white">
                  {contactEmail}
                </a>
              </p>
              <p>
                {text("footer.contact.x")}:{" "}
                <a href={officialXProfileUrl} target="_blank" rel="me noopener noreferrer" className="font-extrabold text-[#67e8f9] no-underline hover:text-white">
                  {officialXHandle}
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className={hongKong ? "mt-12" : "mt-20"}>
          <div
            className="h-px w-full"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(124,58,237,.46) 22%, rgba(217,70,239,.42) 50%, rgba(124,58,237,.46) 78%, transparent 100%)",
            }}
          />
          <div className="relative z-30 flex flex-col gap-2 pt-7 text-[13px] font-semibold text-white/30 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-6">
            <span className="mr-auto text-left">{text("footer.copyright")}</span>
            <div className="flex flex-col items-end gap-2 text-right sm:ml-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-4">
              <Link to={privacyPath(lang)} className={legalLinkClass}>
                {text("footer.legal.privacy")}
              </Link>
              <Link to={cookiePath(lang)} className={legalLinkClass}>
                {text("footer.legal.cookie")}
              </Link>
              <Link to={termsPath(lang)} className={legalLinkClass}>
                {text("footer.legal.terms")}
              </Link>
              <Link to={aboutPath(lang)} className={legalLinkClass}>
                {text("footer.legal.about")}
              </Link>
              {lang === "zh" ? (
                <a href={icpRecord.href} className={legalLinkClass}>
                  {icpRecord.label}
                </a>
              ) : null}
              {lang === "zh" && publicSecurityRecord.label ? (
                <a href={publicSecurityRecord.href} className={legalLinkClass}>
                  {publicSecurityRecord.label}
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      </div>
    </footer>
  );
}
